import { ExternalLink } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { SheetFooter } from '@/components/ui/sheet'
import { AddConnectionFields } from './AddConnectionFields'
import { connectionError, duplicateConnection, type useAddConnection } from './useAddConnection'

type State = ReturnType<typeof useAddConnection>
export function AddConnectionFeedback({ state }: { state: State }) {
  const duplicate = duplicateConnection(state.mutation.error)
  const error = state.restore.error ?? state.start.error ?? state.mutation.error
  return (
    <>
      {duplicate && (
        <Alert variant="warning" className="flex flex-wrap items-center justify-between gap-3">
          <span>Already connected as {duplicate.display_name}.</span>
          {duplicate.restorable ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={state.restore.isPending}
              onClick={() => state.restore.mutate(duplicate.id)}
            >
              Restore {duplicate.display_name}
            </Button>
          ) : (
            <Button asChild variant="link" size="inline">
              <a href={`/settings/connections/${duplicate.id}`}>{duplicate.display_name}</a>
            </Button>
          )}
        </Alert>
      )}
      {error && (!duplicate || state.restore.error) && <Alert>{connectionError(error)}</Alert>}
    </>
  )
}
export function AddConnectionForm({
  state,
  title,
  pending,
  onClose,
}: {
  state: State
  title: string
  pending: boolean
  onClose: () => void
}) {
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        state.mutation.mutate()
      }}
    >
      {pending && (
        <Alert variant="success">Connected to {title}. Name this connection to finish.</Alert>
      )}
      <AddConnectionFields state={state} keys={!pending} />
      <SheetFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={state.mutation.isPending || !!duplicateConnection(state.mutation.error)}
        >
          {pending ? 'Finish' : 'Test and connect'}
        </Button>
      </SheetFooter>
    </form>
  )
}
export function AddConnectionOAuthStart({
  state,
  title,
  onClose,
}: {
  state: State
  title: string
  onClose: () => void
}) {
  return (
    <>
      <div className="space-y-2 py-4">
        <Button
          className="w-full"
          disabled={state.start.isPending}
          onClick={() => state.start.mutate()}
        >
          <ExternalLink className="mr-2 size-4" strokeWidth={1.5} />
          Connect to {title}
        </Button>
        <p className="text-caption text-muted-foreground">
          Sign in at {title}, then return here to name it.
        </p>
      </div>
      <SheetFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </SheetFooter>
    </>
  )
}
